import { FrostFreeStatus, type FrostFreePeriods, type PatchBuildingRoomsRequestBody } from "@eisox/api/core-api";
import dayjs from "@eisox/dayjs";
import { z } from "@eisox/zod";

import { getDefaultMode, getFrostFreeModes, periodsSchema, type PeriodsType } from "~/features/frost-free";
import type { Permission } from "~/types/auth.type";
import type { EnhancedRoom } from "~/types/room.type";

export type RoomsHolidaysSettingsSchemaType = z.infer<typeof roomsHolidaysSettingsSchema>;
export const roomsHolidaysSettingsSchema = z.object({
  roomIds: z.array(z.string()).min(1),
  mode: z.enum(FrostFreeStatus),
  frostFreePeriods: periodsSchema,
});

export const canAccessRoomsHolidaysSettings = (permissions: Permission) => permissions.room?.frostFree?.update;

export const defaultBulkFrostFreeMode = (
  rooms: EnhancedRoom[],
  areFrostFreePeriodsAllowed: boolean,
): FrostFreeStatus => {
  if (!rooms.length)
    return getDefaultMode(undefined, getFrostFreeModes({ periodsAllowed: areFrostFreePeriodsAllowed }));
  const firstRoom = rooms[0];
  if (!firstRoom) return getDefaultMode(undefined, getFrostFreeModes({ periodsAllowed: areFrostFreePeriodsAllowed }));
  const firstStatus = firstRoom.frostFree.status;
  return rooms.every(r => r.frostFree.status === firstStatus)
    ? getDefaultMode(firstStatus, getFrostFreeModes({ periodsAllowed: areFrostFreePeriodsAllowed }))
    : getDefaultMode(undefined, getFrostFreeModes({ periodsAllowed: areFrostFreePeriodsAllowed }));
};

export const mergePeriods = (periods: FrostFreePeriods[]): FrostFreePeriods[] => {
  if (periods.length === 0) return [];

  const sorted = [...periods].sort((a, b) => dayjs(a.begin).diff(dayjs(b.begin)));

  const mergedPeriods: FrostFreePeriods[] = [];
  const firstPeriod = sorted[0];
  if (!firstPeriod) return [];

  let currentPeriod = { ...firstPeriod };

  for (let i = 1; i < sorted.length; i++) {
    const nextPeriod = sorted[i];
    if (!nextPeriod) continue;

    if (dayjs(currentPeriod.end).isBefore(dayjs(nextPeriod.begin).subtract(1, "day"))) {
      mergedPeriods.push(currentPeriod);
      currentPeriod = { ...nextPeriod };
    } else {
      currentPeriod.end = dayjs(currentPeriod.end).isAfter(dayjs(nextPeriod.end)) ? currentPeriod.end : nextPeriod.end;
    }
  }

  mergedPeriods.push(currentPeriod);

  return mergedPeriods;
};

export const buildPeriodPreviewRooms = (
  selectedRooms: EnhancedRoom[],
  merge: boolean,
  formPeriods: PeriodsType,
): { id: string; name: string; frostFree: { status: FrostFreeStatus; periods: FrostFreePeriods[] } }[] => {
  if (selectedRooms.length === 0) return [];

  const addedPeriods = formPeriods.filter(p => !p.removed).map(p => ({ begin: p.begin, end: p.end }));

  return selectedRooms.map(room => {
    const periods = merge ? mergePeriods([...room.frostFree.periods, ...addedPeriods]) : addedPeriods;
    return {
      ...room,
      frostFree: {
        status: FrostFreeStatus.PERIODS,
        periods,
      },
    };
  });
};

export const getRoomPatchFromRooms = (
  mode: FrostFreeStatus,
  roomIds: string[],
  previews: ReturnType<typeof buildPeriodPreviewRooms>,
): PatchBuildingRoomsRequestBody => {
  if (mode === FrostFreeStatus.ENABLED || mode === FrostFreeStatus.DISABLED) {
    return [{ ids: roomIds, frostFree: { status: mode, periods: [] } }];
  }

  const patchMap = new Map<string, { roomIds: string[]; periods: FrostFreePeriods[] }>();

  for (const { id, frostFree } of previews) {
    const key = frostFree.periods.map(p => `${p.begin}-${p.end}`).join("|");
    if (!patchMap.has(key)) {
      patchMap.set(key, { roomIds: [], periods: frostFree.periods });
    }
    const entry = patchMap.get(key);
    if (entry) {
      entry.roomIds.push(id);
    }
  }

  return Array.from(patchMap.values()).map(group => ({
    ids: group.roomIds,
    frostFree: {
      status: FrostFreeStatus.PERIODS,
      periods: group.periods,
    },
  }));
};
